import React from "react";
import { ShieldCheck, Sun, Droplets, Layers, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TropicalDurabilityFactor() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="tropical-durability-factor" ref={ref} className={`dark bg-background text-foreground py-24 relative overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[10%] left-[5%] w-[400px] h-[400px] bg-blue-900/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium border border-primary/20">
                <ShieldCheck className="w-4 h-4" />
                <span>Engineered for North Queensland</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white">
                The ANV Tropical Defense System
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
                Painting in Townsville requires more than just standard application. 
                Our multi-layered coating system is specifically calibrated to withstand 
                intense UV radiation, high humidity, and salt air corrosion.
              </p>
            </div>

            <div className="grid gap-6">
              {[{
              icon: Sun,
              title: "UV-Reflective Technology",
              desc: "Specialized pigments reflect harsh solar rays, preventing color fading and surface degradation even in 35°C+ heat."
            }, {
              icon: Droplets,
              title: "Hydrophobic & Mold Resistant",
              desc: "Advanced additives create a hostile environment for mold and mildew while repelling moisture from tropical storms."
            }, {
              icon: Layers,
              title: "Elastomeric Flexibility",
              desc: "Coatings that expand and contract with thermal shifts, preventing the hairline cracks common in Queensland homes."
            }].map((item, i) => <div key={i} data-index={i} className="flex gap-4 items-start group">
                  <div className="mt-1 p-2 rounded-lg bg-card border border-border group-hover:border-primary/50 transition-colors">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-1">
                      {item.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="pt-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/GetAQuotePage">
                  Protect Your Property <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="w-full max-w-md mx-auto lg:max-w-none lg:ml-auto">
            <Card className="bg-card/50 backdrop-blur-sm border-border/50 shadow-2xl overflow-hidden">
              <CardHeader className="pb-2 border-b border-border/50 bg-card/30">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Layers className="w-5 h-5 text-primary" />
                  Layered Protection Logic
                </CardTitle>
                <CardDescription>
                  Cross-section of the ANV application standard
                </CardDescription>
              </CardHeader>
              
              <CardContent className="p-6 bg-gradient-to-b from-card/50 to-background/50">
                {}
                <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden border border-border/30 bg-background/50 p-4">
                  <svg viewBox="0 0 400 300" className="w-full h-full" xmlns="http://www.w3.org/2000/svg" aria-label="Diagram showing 3 layers of paint protection: Primer, Elastomeric Mid-coat, and UV Shield Topcoat">
                    <defs>
                      <linearGradient id="sun-rays" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.8" />
                        <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
                      </linearGradient>
                      <filter id="glow">
                        <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
                        <feMerge>
                          <feMergeNode in="coloredBlur" />
                          <feMergeNode in="SourceGraphic" />
                        </feMerge>
                      </filter>
                    </defs>

                    {}
                    <path d="M20 260 L380 260 L380 290 L20 290 Z" fill="hsl(var(--muted))" />
                    <text x="200" y="280" textAnchor="middle" fill="hsl(var(--muted-foreground))" fontSize="10" fontWeight="bold" letterSpacing="1">SUBSTRATE (WALL)</text>

                    {}
                    <path d="M20 230 L380 230 L380 260 L20 260 Z" fill="hsl(var(--secondary))" opacity="0.8" />
                    <text x="40" y="250" fill="hsl(var(--secondary-foreground))" fontSize="10" fontWeight="bold">1. DEEP PRIMER</text>
                    
                    {}
                    <path d="M20 200 L380 200 L380 230 L20 230 Z" fill="hsl(var(--accent))" opacity="0.9" />
                    <text x="40" y="220" fill="hsl(var(--accent-foreground))" fontSize="10" fontWeight="bold">2. ELASTOMERIC BOND</text>

                    {}
                    <path d="M20 170 L380 170 L380 200 L20 200 Z" fill="hsl(var(--primary))" opacity="0.9" />
                    <text x="40" y="190" fill="hsl(var(--primary-foreground))" fontSize="10" fontWeight="bold">3. UV SHIELD TOPCOAT</text>

                    {}
                    <circle cx="320" cy="50" r="20" fill="hsl(var(--primary))" filter="url(#glow)" />
                    <path d="M320 75 L300 165" stroke="hsl(var(--primary))" strokeWidth="2" strokeDasharray="4 2" markerEnd="url(#arrow)" />
                    <path d="M300 165 L260 120" stroke="hsl(var(--primary))" strokeWidth="2" opacity="0.6" />
                    
                    {}
                    <path d="M100 60 C100 60 90 80 100 90 C110 80 100 60 100 60 Z" fill="hsl(200 80% 60%)" />
                    <path d="M120 80 C120 80 110 100 120 110 C130 100 120 80 120 80 Z" fill="hsl(200 80% 60%)" />
                    <path d="M120 115 L120 165" stroke="hsl(200 80% 60%)" strokeWidth="2" strokeDasharray="4 2" />
                    <path d="M120 165 L160 165" stroke="hsl(200 80% 60%)" strokeWidth="2" opacity="0.6" />

                    {}
                    <text x="230" y="110" fill="hsl(var(--primary))" fontSize="10" fontStyle="italic">Reflects 98% UV</text>
                    <text x="140" y="155" fill="hsl(200 80% 60%)" fontSize="10" fontStyle="italic">Repels Moisture</text>
                  </svg>
                </div>
              </CardContent>
              
              <CardFooter className="bg-card/30 border-t border-border/50 p-4">
                <div className="flex items-center justify-between w-full text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                    ANV Certified
                  </span>
                  <span>Low-VOC • Sustainable</span>
                </div>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}